import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler } from "@preveil-api";
import {
  DriveItemType, DocRenderTypes, FileSizeLimits, Message, useAppDispatch, EntryItem, Account, 
  useAppSelector, buildNavigationLink, DriveEntryType, DriveUrlParams, DriveErrorMessages, MessageAnchors, 
  useGetFileSize, DriveUIActionTypes, isVideoTypeSupported, MessageHandlerDisplayType, GlobalErrorMessages,
  KeyCode
} from "src/common";
import { Icon } from "src/components";
import FileViewer from "./FileViewer.component";
import { driveActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  file_entry: EntryItem;
  file_type: string | undefined;
  current_account: Account;
  router_params?: DriveUrlParams;
  file_to_view: File | undefined;
  media_entries: EntryItem[];
  rendering_type: string | undefined;
  doc_container: React.RefObject<HTMLDivElement>;
  view_only: boolean;
  handleAction: ActionHandlerFunction;
};

const MediaGalleryComponent = forwardRef(function MediaGalleryViewer(props: AllProps, ref) {
  const { file_entry, file_type, current_account, router_params, file_to_view, media_entries, rendering_type, doc_container, view_only, handleAction} = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const root_info = useAppSelector((state: RootState) => state.drive.root_info);
  const gallery_index = useAppSelector((state: RootState) => state.drive.gallery_index);
  const new_entry = media_entries[gallery_index];
  const [slide_out_complete, setSlideOutComplete] = useState<boolean>(true);
  const [slide_direction, setSlideDirection] = useState<string>("");
  const [first_file_load, setFirstFileLoad] = useState<boolean>(true);
  const [video_too_large, setVideoTooLarge] = useState<boolean>(false);
  const [video_not_supported, setVideoNotSupported] = useState<boolean>(false);
  const video_disabled = video_not_supported || video_too_large;
  const [video_disabled_text, setVideoDisabledText] = useState<string | null>(null);
  const [file_type_changed, setFileTypeChanged] = useState<boolean>(false);
  const { file_size, getFileSize, file_size_error, resetFileSize } = useGetFileSize(current_account);
  const slide_animator = React.useRef<HTMLDivElement>(null);
  const index_ref = React.useRef<number>(0);
  const disable_click_ref = React.useRef<boolean>(false);
  useImperativeHandle(ref, () => ({ handlePageActions }));

  // Description: Handle keyboard navigation for left and right arrows
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.key === KeyCode.ArrowLeft && handleLeftClick();
      e.key === KeyCode.ArrowRight && handleRightClick();
    };

    window.addEventListener("keydown", handleKeyDown);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Description: Set the current index to match the file entry
  useEffect(() => {
    const index = media_entries.findIndex(entry => entry.id === file_entry.id);
    dispatch(driveActions.setGalleryIndex(index));
  }, [media_entries]);

  // Description: Retrieve file for new gallery index
  useEffect(() => {
    !!new_entry && gallery_index >= 0 && getNewFile();
    index_ref.current = gallery_index;
  }, [gallery_index]);

  // Description: Once file size is retrieved, check validity of the video size or show error 
  useEffect(() => {
    !!file_size_error && dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_downloading_file.replace(MessageAnchors.file_name, new_entry.name))));
    !!file_size && checkVideoSize(file_size);
    resetFileSize();
  }, [file_size, file_size_error]);

  // Description: Handle slide out complete
  useEffect(() => {
    slide_out_complete && handleSlideOutComplete();
  }, [slide_out_complete]);

  // Description: Once slide out is complete, either show the video disabled message or set the render type
  useEffect(() => {
    const ready_to_load = slide_out_complete || first_file_load;
    ready_to_load && video_disabled && showVideoDisabledContainer();
    ready_to_load && !video_disabled && handleSetRenderType();
  }, [slide_out_complete, video_disabled, file_to_view]);

  // Description: Once the file type is set, prepare the file reader and show the doc container
  useEffect(() => {
    file_type_changed && prepareViewingContainer();
  }, [file_type_changed]);

  // Description: Navigate to the new file and start getting the new download
  function getNewFile() {
    showLoading();
    navigateToFileUrl(new_entry);
    dispatch(driveActions.setFileName(new_entry.name));
    if (new_entry.type_label === DriveItemType.Video) {
      if (!isVideoTypeSupported(new_entry.name)) {
        setVideoNotSupported(true);
      } else {
        getFileSize(new_entry.collection_id, new_entry.id);
      }
    } else {
      handleAction({ actionType: DriveUIActionTypes.DownloadFile, params: { collection_id: new_entry.collection_id, file_id: new_entry.id, file_name: new_entry.name}});
    }
  }

  // Description: Set loading to true if the file is a video or tif/tiff image
  function showLoading() {
    if (
      new_entry.type_label === DriveItemType.Video ||
      new_entry.name.toLowerCase().endsWith(".tif") ||
      new_entry.name.toLowerCase().endsWith(".tiff")
    ) {
      handleAction({ actionType: DriveUIActionTypes.SetLoading, params: true });
    }
  }

  // Description: Show doc_container and prepare file reader 
  function prepareViewingContainer() {
    if (!!doc_container.current) {
      doc_container.current.style.display = "flex";
    }
    if (rendering_type === DocRenderTypes.viewer) {
      slideIn(slide_direction);
    }
    !!file_to_view && handleAction({ actionType: DriveUIActionTypes.PrepareFileReader, params: file_to_view });;
  }

  // Description: Set the render type for the file to view
  function handleSetRenderType() {
    if (file_entry === media_entries[gallery_index] && !!file_to_view && !!file_type) {
      handleAction({ actionType: DriveUIActionTypes.SetRenderType, params: { file: file_to_view, type: file_type }});
      setFileTypeChanged(true);
    }
  }

  // Description: Show the video disabled container with the corresponding text
  function showVideoDisabledContainer() {
    const warning_msg = 
      video_too_large ? DriveErrorMessages.error_viewing_large_video.replace(MessageAnchors.file_name, new_entry.name) : 
      video_not_supported ?  DriveErrorMessages.error_viewing_unsupported_video.replace(MessageAnchors.file_name, new_entry.name) : 
      null;
    const download_msg = !view_only ? ` ${DriveErrorMessages.please_download_to_view}` : "";
    const msg = `${warning_msg}${download_msg}`;
    setVideoDisabledText(msg);
    if (!!doc_container.current) {
      doc_container.current.style.display = "none";
    }
    handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
    slideIn(slide_direction);
  }

  // Description: Navigate to the file URL based on the router params
  function navigateToFileUrl(entry: EntryItem) {
    if (!!router_params) {
      const { collection_id, id } = router_params;
      !!root_info && root_info.collection_id &&
        navigate(buildNavigationLink(collection_id, id, DriveEntryType.FILE, entry.id));
    }
  }

  // Description: If video file size is larger than the max allowed, show an error, otherwise show video
  function checkVideoSize(file_size: number) {
    if (file_size > FileSizeLimits.VIDEO_VIEWING_MAX_SIZE) {
      setVideoTooLarge(true);
    } else {
      handleAction({ actionType: DriveUIActionTypes.DownloadFile, params: { collection_id: new_entry.collection_id, file_id: new_entry.id, file_name: new_entry.name}});
    }
  }

  // Description: Reset states and set new index
  function setNewIndex(new_index: number) {
    tempDisableArrowClick();
    reset();
    dispatch(driveActions.setGalleryIndex(new_index));
  }

  // Description: Slide out to the left and update the index
  function handleRightClick() {
    if (disable_click_ref.current) return;
    if (index_ref.current < media_entries.length-1) {
      setNewIndex(Number(index_ref.current) + 1);
      setSlideDirection("right");
      slideOut("left");
    }
  }

  // Description: Slide out to the right and update the index
  function handleLeftClick() {
    if (disable_click_ref.current) return;
    if (index_ref.current > 0) {
      setNewIndex(index_ref.current - 1);
      setSlideDirection("left");
      slideOut("right");
    }
  }

  // Description: Reset all states
  function reset() {
    handleAction({ actionType: DriveUIActionTypes.ResetDownload});
    setVideoTooLarge(false);
    setVideoNotSupported(false);
    resetFileSize();
    setSlideOutComplete(false);
    setFileTypeChanged(false);
    setFirstFileLoad(false);
  }

  // Description: Removes classes for animating sliding in
  function removeSlideInClasses() {
    if (!!slide_animator.current) {
      slide_animator.current.classList.remove("slide-in-right");
      slide_animator.current.classList.remove("slide-in-left");
    }
  }
  
  // Description: Removes classes for animating sliding out
  function removeSlideOutClasses() {
    if (!!slide_animator.current) {
      slide_animator.current.classList.remove("slide-out-left");
      slide_animator.current.classList.remove("slide-out-right");
    }
  }

  // Description: Reset doc_container and remove old classes
  function handleSlideOutComplete() {
    setVideoDisabledText(null);
    if (!!doc_container.current) {
      doc_container.current.innerHTML = "";
      doc_container.current.style.backgroundImage = "";
      doc_container.current.className = "";
    }
  }

  // Description: Slide-in animation
  function slideIn(direction: string, video?: HTMLVideoElement) {
    if (!first_file_load) {
      const file_extension = file_entry.name.split(".").pop()?.toLowerCase() || "";
      const video_types_no_slide = ["ogg", "ogv"];
      // Do not disable video controls for .ogg and .ogv because for these types disabling controls disables the whole video
      !!video && !video_types_no_slide.includes(file_extension) && (video.controls = false);
      removeSlideOutClasses();
      slide_animator.current && slide_animator.current.classList.add(direction === "left" ? "slide-in-left" : "slide-in-right");
      setTimeout(() => {
        // Only enable controls once video is slid in to prevent the sliding from affecting the video controls width
        !!video && (video.controls = true);
      }, 300);
    } 
  }

  // Description: Slide-out animation
  function slideOut(direction: string) {
    removeSlideInClasses();
    !!slide_animator.current && slide_animator.current.classList.add(direction === "left" ? "slide-out-left" : "slide-out-right");
    setTimeout(() => {
      setSlideOutComplete(true);
    }, 300);
  }

  // Description: Trigger disable click for 250ms to prevent fast clicking
  function tempDisableArrowClick() {
    disable_click_ref.current = true;
    setTimeout(() => {
      disable_click_ref.current = false;
    }, 250);
  }

  // Description: Handle all children component actions and store it
  const MediaGalleryRequests = {
    // Description: Slide in the file
    handleSlideIn: (params?: { video?: HTMLVideoElement }) => {
      slideIn(slide_direction, params?.video);
    },
    // Description: Show error message 
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  // Description: Handle all actions from Children forms
  function handlePageActions(actionObj: IActionHandler) {
    const callback = `handle${actionObj.actionType}`;
    if ((MediaGalleryRequests as any)[callback] instanceof Function) {
      (MediaGalleryRequests as any)[callback](actionObj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, actionObj.actionType);
      MediaGalleryRequests.handlePageErrorMessage({ message, stack: actionObj });
    }
  }

  return (
    <>
      <div id="gallery-container">
        <button onMouseDown={(e) => e.preventDefault()} className={`arrow ${gallery_index === 0 ? 'invisible' : ''}`}
          onClick={handleLeftClick}>
          <Icon className="arrow-icon ficon-chevron-left"/>
        </button>
        <div id="doc-outer-container" className="media-gallery">
          <div id="slide-animator" ref={slide_animator}>
            {!!file_to_view && rendering_type === DocRenderTypes.viewer && !!file_type && !video_disabled_text && (
              <FileViewer
                file={file_to_view}
                type={file_type}
                is_view_only={view_only}
                handleAction={handleAction}
              />
            )}
            <div id="doc-container" ref={doc_container}></div>
            {video_disabled_text && (
              <div className="video-disabled-container">
                <div className="video-disabled-message"> 
                  {video_disabled_text}
                </div>
                <div>
                {!view_only && (
                  <Button className="download-btn" aria-label={`Download File`}
                    onClick={() => handleAction({ actionType: DriveUIActionTypes.Download, params: new_entry })}>
                    <Icon className="ficon-download" />
                    <span>Download File</span>
                  </Button>
                )}
                </div>
              </div>
            )}
          </div> 
        </div>
        <button onMouseDown={(e) => e.preventDefault()} className={`arrow ${gallery_index >= media_entries.length - 1 ? 'invisible' : ''}`}
          onClick={handleRightClick}>
          <Icon className="arrow-icon ficon-chevron-right"/>
        </button>
      </div>
    </>
  );
});

export default React.memo(MediaGalleryComponent);
